import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { ParkingLotItem } from "@data/types";

type ParkingLotContextValue = {
  items: ParkingLotItem[];
  addItem: (text: string) => void;
  resolveItem: (id: string) => void;
  setFollowup: (
    id: string,
    followup: { name: string; email?: string; notes?: string },
  ) => void;
  removeItem: (id: string) => void;
  exportMarkdown: () => string;
  isDrawerOpen: boolean;
  openDrawer: () => void;
  closeDrawer: () => void;
  toggleDrawer: () => void;
};

const ParkingLotContext = createContext<ParkingLotContextValue | null>(null);

export function useParkingLot(): ParkingLotContextValue {
  const ctx = useContext(ParkingLotContext);
  if (!ctx)
    throw new Error("useParkingLot must be used within ParkingLotProvider");
  return ctx;
}

type Props = {
  customerId: string;
  children: ReactNode;
};

function loadItems(customerId: string): ParkingLotItem[] {
  try {
    const raw = localStorage.getItem(`na_parking_${customerId}`);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveItems(customerId: string, items: ParkingLotItem[]) {
  localStorage.setItem(`na_parking_${customerId}`, JSON.stringify(items));
}

export function ParkingLotProvider({ customerId, children }: Props) {
  const [items, setItems] = useState<ParkingLotItem[]>(() =>
    loadItems(customerId),
  );
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  useEffect(() => {
    saveItems(customerId, items);
  }, [customerId, items]);

  const openDrawer = useCallback(() => setIsDrawerOpen(true), []);
  const closeDrawer = useCallback(() => setIsDrawerOpen(false), []);
  const toggleDrawer = useCallback(() => setIsDrawerOpen((v) => !v), []);

  const addItem = useCallback((text: string) => {
    const item: ParkingLotItem = {
      id: crypto.randomUUID(),
      text,
      status: "open",
      createdAt: new Date().toISOString(),
    };
    setItems((prev) => [item, ...prev]);
  }, []);

  const resolveItem = useCallback((id: string) => {
    setItems((prev) =>
      prev.map((item) =>
        item.id === id
          ? { ...item, status: "resolved" as const, resolvedAt: new Date().toISOString() }
          : item,
      ),
    );
  }, []);

  const setFollowup = useCallback(
    (
      id: string,
      followup: { name: string; email?: string; notes?: string },
    ) => {
      setItems((prev) =>
        prev.map((item) =>
          item.id === id
            ? { ...item, status: "needs-followup" as const, followup }
            : item,
        ),
      );
    },
    [],
  );

  const removeItem = useCallback((id: string) => {
    setItems((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const exportMarkdown = useCallback(() => {
    const lines = ["# Parking Lot Items\n"];
    const grouped = {
      open: items.filter((i) => i.status === "open"),
      "needs-followup": items.filter((i) => i.status === "needs-followup"),
      resolved: items.filter((i) => i.status === "resolved"),
    };

    if (grouped.open.length) {
      lines.push("## Open\n");
      grouped.open.forEach((i) => lines.push(`- [ ] ${i.text}`));
      lines.push("");
    }
    if (grouped["needs-followup"].length) {
      lines.push("## Needs Follow-up\n");
      grouped["needs-followup"].forEach((i) => {
        lines.push(`- [ ] ${i.text}`);
        if (i.followup) {
          lines.push(`  - **Owner**: ${i.followup.name}`);
          if (i.followup.email) lines.push(`  - **Email**: ${i.followup.email}`);
          if (i.followup.notes) lines.push(`  - **Notes**: ${i.followup.notes}`);
        }
      });
      lines.push("");
    }
    if (grouped.resolved.length) {
      lines.push("## Resolved\n");
      grouped.resolved.forEach((i) => lines.push(`- [x] ${i.text}`));
      lines.push("");
    }

    return lines.join("\n");
  }, [items]);

  const value = useMemo(
    () => ({
      items,
      addItem,
      resolveItem,
      setFollowup,
      removeItem,
      exportMarkdown,
      isDrawerOpen,
      openDrawer,
      closeDrawer,
      toggleDrawer,
    }),
    [
      items,
      addItem,
      resolveItem,
      setFollowup,
      removeItem,
      exportMarkdown,
      isDrawerOpen,
      openDrawer,
      closeDrawer,
      toggleDrawer,
    ],
  );

  return (
    <ParkingLotContext.Provider value={value}>
      {children}
    </ParkingLotContext.Provider>
  );
}
